import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import { convertPocketRubric, fetchLinkTargets, linkPocketRubric, pocketErrorMessage } from './pocketApi';
import { buildPocketTrace, TRACE_STATUS } from './pocketTrace';
import { pocketTint } from './pocketTint';

export const POCKET_NOTICE_TEXT = 'Des données proviennent de BlueNote Pocket';

const personLabel = (p) => (p.isUnknown ? 'Inconnu' : `${(p.lastname || '').toUpperCase()} ${p.firstname || ''}`.trim());
const intervenantLabel = (i) => `${(i.lastname || '').toUpperCase()} ${i.firstname || ''}${i.rank ? `, ${i.rank}` : ''}`.trim();

/** Libellés courants des entités du document, par identifiant. */
export const documentEntityLabels = (report) => {
  const labels = new Map();
  (report?.personnesPhysiques || []).forEach((p) => labels.set(String(p._id), personLabel(p)));
  (report?.intervenants || []).forEach((i) => labels.set(String(i._id), intervenantLabel(i)));
  return labels;
};

/** Révisions plus récentes jointes lors d'imports ultérieurs (jamais appliquées automatiquement). */
export const attachedRevisions = (report, pocket) => (report?.pocketImports || []).flatMap((imp) =>
  (imp.items || [])
    .filter((it) => it.occurrenceId === pocket.occurrenceId && it.decision === 'attachRevision' && it.revision > pocket.revision)
    .map((it) => ({ ...it, importedAt: imp.importedAt, importedBy: imp.importedBy }))
);

const formatDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const NOTE_COLOR = {
  [TRACE_STATUS.unmapped]: 'warning.dark',
  [TRACE_STATUS.rejected]: 'error.main',
  [TRACE_STATUS.empty]: 'text.secondary',
  [TRACE_STATUS.used]: 'text.secondary',
  [TRACE_STATUS.mapped]: 'text.secondary'
};

function PocketTraceTable({ rows, isDark }) {
  const cell = { verticalAlign: 'top', py: 0.75 };
  return (
    <TableContainer
      sx={{
        mt: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(2, 136, 209, 0.25)',
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.75)',
        '& .MuiTableCell-root': { color: 'inherit', borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(2, 136, 209, 0.15)' }
      }}
    >
      <Table size="small" aria-label="Données reprises de BlueNote Pocket">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: '38%' }}>Encodé dans Pocket</TableCell>
            <TableCell sx={{ fontWeight: 700, width: '30%' }}>Traduit en</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Repris dans le champ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const source = (
              <TableCell rowSpan={Math.max(row.targets.length, 1)} sx={cell}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                {row.value && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.value}</Typography>}
              </TableCell>
            );
            if (row.targets.length === 0) {
              return (
                <TableRow key={row.key}>
                  {source}
                  <TableCell sx={cell}><Typography variant="body2">{row.entity || '—'}</Typography></TableCell>
                  <TableCell sx={cell}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: NOTE_COLOR[row.status] }}>{row.note}</Typography>
                  </TableCell>
                </TableRow>
              );
            }
            return row.targets.map((target, index) => (
              <TableRow key={`${row.key}-${target.field}`}>
                {index === 0 && source}
                <TableCell sx={cell}><Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{target.value}</Typography></TableCell>
                <TableCell sx={cell}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{target.label}</Typography>
                  {index === row.targets.length - 1 && row.note && (
                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'warning.dark' }}>{row.note}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RevisionData({ display }) {
  const lines = []
    .concat((display.times || []).filter((t) => t.state !== 'absent').map((t) => [t.label, t.display]))
    .concat((display.answers || []).map((a) => [a.label, a.display]))
    .concat(typeof display.notes === 'string' && display.notes.length > 0 ? [[display.notesLabel || 'Précisions', display.notes]] : []);
  return lines.map(([label, value]) => (
    <Typography key={label} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box> : {value}
    </Typography>
  ));
}

function LinkDialog({ open, onClose, code, definitionVersion, onConfirm, busy }) {
  const [targets, setTargets] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    setSelected(null);
    try {
      setTargets(await fetchLinkTargets(code || '', definitionVersion));
    } catch (err) {
      setError(pocketErrorMessage(err, 'Impossible de charger la liste des rubriques.'));
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" TransitionProps={{ onEnter: load }}>
      <DialogTitle>Rattacher à une rubrique BlueNote</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!targets && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
        {targets && (
          <>
            {targets.suggested.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Rubriques prévues pour cet événement (préremplissage disponible) :</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {targets.suggested.map((t) => (
                    <Chip
                      key={t.version}
                      label={t.title}
                      color={selected?.version === t.version ? 'primary' : 'default'}
                      onClick={() => setSelected({ version: t.version, title: t.title })}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Autocomplete
              options={targets.all}
              getOptionLabel={(o) => o.title}
              isOptionEqualToValue={(a, b) => a.version === b.version}
              value={selected ? targets.all.find((o) => o.version === selected.version) || null : null}
              onChange={(e, value) => setSelected(value ? { version: value.version, title: value.title } : null)}
              renderInput={(params) => <TextField {...params} label="Toutes les rubriques à générer" />}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
              Une rubrique sans correspondance prévue ne reçoit aucune valeur : les données Pocket restent consultables dans le détail.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="contained" disabled={!selected || busy} onClick={() => onConfirm(selected.version)}>
          {busy ? <CircularProgress size={18} /> : 'Rattacher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Mention discrète au-dessus d'une rubrique Pocket non générée ; le détail montre « encodé → traduit → repris dans ». */
function PocketRubricNotice({ rubric, report, isDark }) {
  const pocket = rubric.pocket;
  const [expanded, setExpanded] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const labels = useMemo(() => documentEntityLabels(report), [report]);
  const trace = useMemo(() => (pocket && pocket.display ? buildPocketTrace(pocket, labels) : null), [pocket, labels]);
  const revisions = useMemo(() => (pocket ? attachedRevisions(report, pocket) : []), [report, pocket]);

  if (!pocket || !trace || rubric.generated) return null;
  const status = pocket.status;
  const unresolved = status === 'unresolved';
  const canAct = pocket.kind === 'event' && !rubric.isEditing && ['unresolved', 'linked'].includes(status);

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      setLinkOpen(false);
      setConfirmConvert(false);
    } catch (err) {
      setError(pocketErrorMessage(err, 'L’opération n’a pas pu être effectuée.'));
    } finally {
      setBusy(false);
    }
  };

  const actions = canAct && (
    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
      <Button size="small" variant="contained" startIcon={<LinkIcon />} onClick={() => setLinkOpen(true)} disabled={busy}>
        Rattacher à une rubrique…
      </Button>
      <Button size="small" variant="outlined" color="inherit" startIcon={<NotesIcon />} onClick={() => setConfirmConvert(true)} disabled={busy}>
        Convertir en texte libre
      </Button>
    </Box>
  );

  return (
    <Alert
      severity={unresolved ? 'warning' : 'info'}
      icon={<PhoneIphoneIcon fontSize="inherit" />}
      sx={{
        mb: 1,
        py: 0,
        px: 1.5,
        '& .MuiAlert-icon': { py: 0.5, mr: 1, fontSize: 18 },
        '& .MuiAlert-message': { width: '100%', py: 0.5 },
        backgroundColor: unresolved ? (isDark ? 'rgba(237, 108, 2, 0.12)' : undefined) : pocketTint(isDark),
        color: isDark ? '#ecf0f1' : undefined
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{POCKET_NOTICE_TEXT}</Typography>
        {unresolved && <Typography variant="body2">— choisissez la rubrique correspondante.</Typography>}
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="always"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          sx={{ color: 'inherit', opacity: 0.8, verticalAlign: 'baseline' }}
        >
          {expanded ? 'masquer le détail' : 'détail'}
        </Link>
      </Box>
      {unresolved && actions}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            {status === 'mapped' && `${pocket.display.label} : les données ci-dessous sont reprises dans le formulaire « Générer », sur fond bleuté. Vérifiez-les avant de générer.`}
            {status === 'linked' && `${pocket.display.label} : aucune reprise automatique pour cette rubrique, recopiez les données utiles.`}
            {unresolved && (pocket.reason || 'Aucune rubrique BlueNote certaine pour cet événement.')}
          </Typography>
          {pocket.split && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Rubrique {pocket.split.index} sur {pocket.split.count} : une rubrique par personne concernée.
            </Typography>
          )}
          <PocketTraceTable rows={trace.rows} isDark={isDark} />
          {Array.isArray(pocket.remarks) && pocket.remarks.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              {pocket.remarks.map((r) => <Typography component="li" key={r} variant="body2">{r}</Typography>)}
            </Box>
          )}
          {revisions.map((r) => (
            <Box key={`${r.revision}-${r.importedAt}`} sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Révision {r.revision} jointe le {formatDate(r.importedAt)} : elle ne modifie rien automatiquement.
              </Typography>
              {r.display && <RevisionData display={r.display} />}
            </Box>
          ))}
          {status === 'linked' && actions}
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
            Dossier Pocket « {pocket.folderTitle} » — importé le {formatDate(pocket.importedAt)}
            {pocket.revision ? ` — révision ${pocket.revision}` : ''}
          </Typography>
        </Box>
      </Collapse>

      <LinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        code={pocket.code}
        definitionVersion={pocket.definitionVersion}
        busy={busy}
        onConfirm={(version) => run(() => linkPocketRubric(report._id, rubric.id, version))}
      />
      <Dialog open={confirmConvert} onClose={busy ? undefined : () => setConfirmConvert(false)}>
        <DialogTitle>Convertir en texte libre ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Les données Pocket de cet élément deviennent une rubrique de texte libre modifiable. Cette action ne peut pas être annulée.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmConvert(false)} disabled={busy}>Annuler</Button>
          <Button variant="contained" onClick={() => run(() => convertPocketRubric(report._id, rubric.id))} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Convertir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Alert>
  );
}

export default PocketRubricNotice;
